import type { ModuleKey } from './modules.js';

/**
 * The complete permission catalogue for every phase.
 * Roles store lists of these keys. Defining the whole catalogue now keeps role
 * definitions stable as later phases ship; each group records the phase that
 * delivers the screens it controls, and the UI labels groups that are not built yet.
 */

export interface PermissionDef {
  key: string;
  label: string;
  /** Permission only applies when this module is enabled for the business. */
  module?: ModuleKey;
  /** Sensitive permissions are highlighted in the role editor. */
  sensitive?: boolean;
}

export interface PermissionGroup {
  key: string;
  label: string;
  phase: number;
  permissions: PermissionDef[];
}

export const PERMISSION_GROUPS = [
  {
    key: 'admin',
    label: 'Administration',
    phase: 1,
    permissions: [
      { key: 'dashboard.view', label: 'View the overview dashboard' },
      { key: 'settings.manage', label: 'Change business settings and modules', sensitive: true },
      { key: 'branches.manage', label: 'Add and edit branches' },
      { key: 'users.view', label: 'View staff accounts' },
      { key: 'users.manage', label: 'Create, edit, deactivate staff and reset passwords', sensitive: true },
      { key: 'roles.manage', label: 'Create and edit roles and permissions', sensitive: true },
      { key: 'audit.view', label: 'View the audit log' },
    ],
  },
  {
    key: 'pos',
    label: 'Point of sale',
    phase: 2,
    permissions: [
      { key: 'pos.sell', label: 'Ring up sales' },
      { key: 'pos.discount', label: 'Apply discounts' },
      { key: 'pos.price_override', label: 'Override selling prices', sensitive: true },
      { key: 'pos.void_line', label: 'Remove lines from an open sale' },
      { key: 'pos.void_sale', label: 'Void a completed sale', sensitive: true },
      { key: 'pos.return', label: 'Process returns and refunds', sensitive: true },
      { key: 'pos.reprint', label: 'Reprint receipts' },
    ],
  },
  {
    key: 'inventory',
    label: 'Products & stock',
    phase: 2,
    permissions: [
      { key: 'products.view', label: 'View products and prices' },
      { key: 'products.manage', label: 'Create and edit products and prices' },
      { key: 'products.view_cost', label: 'See cost prices and margins' },
      { key: 'stock.view', label: 'View stock levels and movements' },
      { key: 'stock.receive', label: 'Receive stock from suppliers' },
      { key: 'stock.adjust', label: 'Make stock adjustments', sensitive: true },
      { key: 'stock.transfer', label: 'Transfer stock between branches' },
      { key: 'stock.stocktake', label: 'Capture stocktake counts' },
      { key: 'stock.stocktake_approve', label: 'Approve stocktakes and post variances', sensitive: true },
      { key: 'suppliers.view', label: 'View suppliers' },
      { key: 'suppliers.manage', label: 'Create and edit suppliers' },
      { key: 'purchases.manage', label: 'Create and approve purchase orders' },
    ],
  },
  {
    key: 'finance',
    label: 'Money & credit',
    phase: 3,
    permissions: [
      { key: 'customers.view', label: 'View customers and balances' },
      { key: 'customers.manage', label: 'Create and edit customers' },
      { key: 'credit.grant', label: 'Sell on account and set credit limits', sensitive: true },
      { key: 'credit.receive_payment', label: 'Receive customer account payments' },
      { key: 'supplier_payments.manage', label: 'Record supplier invoices and payments', sensitive: true },
      { key: 'expenses.record', label: 'Record expenses' },
      { key: 'expenses.approve', label: 'Approve expenses', sensitive: true },
      { key: 'shifts.operate', label: 'Open and close own till shift' },
      { key: 'shifts.reconcile', label: 'Reconcile and sign off shifts', sensitive: true },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    phase: 3,
    permissions: [
      { key: 'reports.sales', label: 'Sales reports' },
      { key: 'reports.profit', label: 'Gross profit reports', sensitive: true },
      { key: 'reports.stock', label: 'Stock valuation and discrepancy reports' },
      { key: 'reports.financial', label: 'Debtors, creditors and cash reports' },
    ],
  },
  {
    key: 'specialised',
    label: 'Business-specific',
    phase: 4,
    permissions: [
      { key: 'expiry.manage', label: 'Manage batches and expiry dates', module: 'expiry' },
      { key: 'promotions.manage', label: 'Create and edit promotions', module: 'promotions' },
      { key: 'yields.manage', label: 'Record carcass breakdowns and yields', module: 'yields' },
      { key: 'wastage.record', label: 'Record wastage', module: 'wastage' },
      { key: 'variants.manage', label: 'Manage product variants', module: 'variants' },
      { key: 'orders.manage', label: 'Take orders and deposits', module: 'orders' },
      { key: 'deliveries.manage', label: 'Schedule and update deliveries', module: 'deliveries' },
    ],
  },
  {
    key: 'compliance',
    label: 'Currency & fiscalisation',
    phase: 5,
    permissions: [
      { key: 'currency.rates_manage', label: 'Set exchange rates', sensitive: true },
      { key: 'fiscal.manage', label: 'Manage ZIMRA fiscal device and fiscal days', sensitive: true },
    ],
  },
] as const satisfies readonly PermissionGroup[];

type Groups = typeof PERMISSION_GROUPS;
export type Permission = Groups[number]['permissions'][number]['key'];

export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.key),
) as Permission[];

const PERMISSION_SET = new Set<string>(ALL_PERMISSIONS);
export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

export const PERMISSION_INDEX: Record<string, PermissionDef & { group: string; phase: number }> = Object.fromEntries(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => [p.key, { ...p, group: g.key, phase: g.phase }])),
);

/** Built-in roles created for every new business. */
export interface SystemRoleDef {
  key: string;
  name: string;
  description: string;
  permissions: Permission[];
}

const all = ALL_PERMISSIONS;
const without = (excluded: Permission[]) => all.filter((p) => !excluded.includes(p));

export const OWNER_ROLE_KEY = 'owner';

export const SYSTEM_ROLES: SystemRoleDef[] = [
  {
    key: OWNER_ROLE_KEY,
    name: 'Owner',
    description: 'Full access to everything, including settings, staff and financial reports.',
    permissions: all,
  },
  {
    key: 'manager',
    name: 'Manager',
    description: 'Runs the shop day to day: sales, stock, staff and reports. Cannot change roles or business settings.',
    permissions: without(['settings.manage', 'roles.manage', 'fiscal.manage']),
  },
  {
    key: 'cashier',
    name: 'Cashier',
    description: 'Sells at the till and operates their own shift.',
    permissions: ['pos.sell', 'pos.void_line', 'pos.reprint', 'products.view', 'stock.view', 'customers.view', 'shifts.operate'],
  },
  {
    key: 'stock_controller',
    name: 'Stock Controller',
    description: 'Receives stock, counts stock and manages suppliers.',
    permissions: [
      'products.view',
      'products.manage',
      'products.view_cost',
      'stock.view',
      'stock.receive',
      'stock.adjust',
      'stock.transfer',
      'stock.stocktake',
      'suppliers.view',
      'suppliers.manage',
      'purchases.manage',
      'reports.stock',
      'expiry.manage',
      'yields.manage',
      'wastage.record',
      'variants.manage',
    ],
  },
  {
    key: 'accountant',
    name: 'Accountant',
    description: 'Manages money: credit, supplier payments, expenses, reconciliation and financial reports.',
    permissions: [
      'dashboard.view',
      'products.view',
      'products.view_cost',
      'stock.view',
      'suppliers.view',
      'customers.view',
      'customers.manage',
      'credit.receive_payment',
      'supplier_payments.manage',
      'expenses.record',
      'expenses.approve',
      'shifts.reconcile',
      'reports.sales',
      'reports.profit',
      'reports.stock',
      'reports.financial',
      'audit.view',
      'users.view',
    ],
  },
];
